/**
 * What the assistant knows about Buildmate itself (SCRUM-223).
 *
 * The tutorial half, and the strongest reason the feature was asked for: a
 * product with this much in it has a discoverability problem, and "ask it
 * what this does" beats a tour nobody finishes.
 *
 * **Written here rather than loaded from `docs/features`.** Those are
 * 136KB of acceptance criteria, frame references and decision records —
 * most of it is for whoever builds the next change, not for somebody
 * asking how change orders work, and bundling it would put all of it in
 * every browser. These are the same facts, said once, for a reader.
 *
 * Kept deliberately short per topic. The model relays what it is given,
 * so a long entry becomes a long answer to a short question.
 */
export interface ProductTopic {
  readonly key: string;
  /** Words somebody might actually ask about, matched loosely. */
  readonly matches: readonly string[];
  readonly summary: string;
}

export const PRODUCT_TOPICS: readonly ProductTopic[] = [
  {
    key: 'jobs',
    matches: ['job', 'jobs', 'project', 'lead', 'status', 'archive'],
    summary:
      'Everything in Buildmate hangs off a job. A job has a name, a prefix used to number its documents (GLN-0001), an address, dates and a status: lead, open, closed or archived. Edit it from the job summary with the Edit job button. The prefix cannot be changed once the job exists, because it is printed into every purchase order and invoice number and suppliers are holding those references.',
  },
  {
    key: 'schedule',
    matches: ['schedule', 'gantt', 'calendar', 'dates', 'activity', 'phase', 'dependency', 'lag'],
    summary:
      'A job’s work, as items with a start date and a number of work days. The end date is derived from the job’s work week and its holidays, never typed. Items can depend on each other (finish-to-start and the other three types, with lag), group into phases, and show in three views: list, calendar and Gantt. A schedule is offline until somebody publishes it; while offline no sub or homeowner sees it and no reminders go out.',
  },
  {
    key: 'equipment',
    matches: ['equipment', 'plant', 'machine', 'excavator', 'fleet', 'tool'],
    summary:
      'The company’s plant, under Settings → Equipment: unit name, kind of machine, what it cost, when it was bought and the cost code its depreciation is charged to. Book a machine onto a schedule item and the booking follows that item’s dates — move the bar and the plant moves with it. If the same machine is wanted by overlapping work, the picker warns while the dates are being typed. It warns and does not refuse, because two jobs wanting one excavator on a Tuesday is sometimes half a day each.',
  },
  {
    key: 'materials',
    matches: ['material', 'materials', 'supplies', 'catalog item'],
    summary:
      'What a job needs on site, added to a schedule item from the catalog with a quantity, and rolled up on the job’s Materials tab by material with the first date any of it is wanted. It is a list to work from — it orders nothing and costs nothing to the job. Ordering is a purchase order; costing is the budget.',
  },
  {
    key: 'templates',
    matches: ['template', 'templates', 'reuse', 'standard build'],
    summary:
      'A saved skeleton of a schedule: phases, items, offsets in work days, dependencies, the kinds of machine each item needs and the materials it takes. Save one from a job’s schedule, then apply it to another job with a start date. Applying is additive and tells you how many items it added. It books a real machine for each kind the template asks for — a free one if there is one, otherwise a busy one with the conflict warning.',
  },
  {
    key: 'changeOrders',
    matches: ['change order', 'change orders', 'variation', 'extra'],
    summary:
      'A priced change to the contract, approved by the homeowner. The owner sees a copy carrying the price and no cost at all — a separate document rather than a hidden field, so the builder’s cost is not in the record they read. An approved change order moves the job’s running total and the budget’s revised figures.',
  },
  {
    key: 'selections',
    matches: ['selection', 'selections', 'allowance', 'choice'],
    summary:
      'Choices the homeowner makes, each with an allowance. Picking something over the allowance bills the difference, not the whole price — the allowance is already in the contract. A selection can carry a deadline tied to the schedule item that depends on it.',
  },
  {
    key: 'estimate',
    matches: ['estimate', 'estimating', 'price', 'markup', 'bid'],
    summary:
      'What a job is priced at, line by line against cost codes, with a markup per line. Approving an estimate sets the job’s original budget and makes the estimate read-only; a new version carries the lines across. Every line needs a cost code before it can be approved.',
  },
  {
    key: 'budget',
    matches: ['budget', 'cost', 'actual', 'variance', 'job costing'],
    summary:
      'Original against revised against actual, per cost code. Original comes from the approved estimate; revised adds approved change orders and selection differences; actual comes from bills and receipts. The homeowner sees a separate copy with prices and no costs.',
  },
  {
    key: 'purchaseOrders',
    matches: ['purchase order', 'po', 'order', 'supplier', 'sub'],
    summary:
      'What has been committed to a supplier or sub, numbered from the job prefix (GLN-0001). A released order shows in the budget as committed. A sub can bill against one, and the bill cannot exceed what is left on it.',
  },
  {
    key: 'bills',
    matches: ['bill', 'bills', 'invoice from', 'payable'],
    summary:
      'What the company owes: a sub’s invoice or a supplier’s, costed to cost codes and flowing into the budget’s actual. Marking one ready for payment needs a specific permission, because it is the decision that releases money.',
  },
  {
    key: 'invoices',
    // `bill` is here as well as on bills: "bill the homeowner" is an
    // invoice and "a bill from a sub" is a payable, and the word alone
    // cannot tell them apart. Both come back and the model picks from the
    // rest of the sentence, which is the whole reason this returns
    // several topics rather than one.
    matches: ['invoice', 'invoices', 'billing', 'bill', 'receivable', 'payment', 'charge'],
    summary:
      'What the homeowner owes, numbered from the job prefix. Lines can be drawn from the estimate, a selection or the catalog. Tax is frozen onto each invoice at the rate in force when it was raised, so a later rate change does not rewrite an invoice somebody has already paid.',
  },
  {
    key: 'payments',
    matches: ['payment', 'payments', 'paid', 'deposit', 'credit memo', 'retention'],
    summary:
      'What the homeowner has actually paid against an invoice, plus deposits taken up front and credit memos issued. Recording a payment is a separate permission from raising the invoice. Credits and deposits are shown on the invoice rather than quietly folded into the balance, so the figure can be explained.',
  },
  {
    key: 'dailyLogs',
    matches: ['daily log', 'log', 'site diary', 'weather'],
    summary:
      'What happened on site, day by day, with photographs, weather and who was there. Each log has an audience: internal only, or shared with subs or the homeowner.',
  },
  {
    key: 'todos',
    matches: ['to-do', 'todo', 'task', 'checklist', 'due'],
    summary:
      'What has to happen and when, with a checklist inside each one. A to-do can be due on a date or when a schedule item finishes, so it moves when the work does.',
  },
  {
    key: 'files',
    matches: ['file', 'files', 'document', 'photo', 'folder'],
    summary:
      'Documents, photographs and video, in folders, each with its own audience. A file shared with the homeowner appears in their portal; one that is not, does not.',
  },
  {
    key: 'portal',
    matches: ['portal', 'homeowner', 'owner', 'client', 'customer'],
    summary:
      'What the homeowner sees: their schedule, their invoices, their change orders to approve, selections to choose and the files shared with them. They never see builder cost. Where a record carries cost, the portal reads a separate copy written without it rather than relying on a flag.',
  },
  {
    key: 'permissions',
    matches: ['permission', 'role', 'access', 'who can', 'member', 'invite'],
    summary:
      'Six starting roles — administrator, project manager, office and accounting, field, subcontractor, homeowner — each a set of module levels (none, view, edit, admin) plus flags like seeing builder cost. They are templates: an administrator can clone and edit them. Permissions are enforced by security rules on the database, not only by hiding buttons.',
  },
  {
    key: 'safety',
    matches: ['safety', 'osha', 'notice', 'acknowledge', 'toolbox'],
    summary:
      'A notice everybody named has to acknowledge before carrying on — it cannot be clicked past. Changing the wording asks everybody again, including people who acknowledged the old text, so "everybody has acknowledged" always means this wording. Who acknowledged what, and when, is on the notice under Who, and when, and exports as a file.',
  },
  {
    key: 'costCodes',
    matches: ['cost code', 'chart of accounts', 'category', 'catalog'],
    summary:
      'The chart of accounts every job is costed against, company-wide so cross-job reporting is possible. The catalog is priced items and assemblies to estimate from. Cost codes are retired rather than deleted, because a bill from two years ago points at one.',
  },
  {
    key: 'timeClock',
    matches: ['time clock', 'timeclock', 'time tracking', 'hours', 'timesheet', 'clock in'],
    summary:
      'Hours worked on a job, clocked in and out by the people doing the work. Approving a shift is a separate permission from recording one, because approving is what makes it payable.',
  },
  {
    key: 'warranties',
    matches: ['warranty', 'warranties', 'defect', 'snag', 'punch list'],
    summary:
      'Work to put right after handover, raised against a job and assigned to whoever has to do it. A sub can be given one to answer without being given the rest of the job.',
  },
  {
    key: 'rfis',
    matches: ['rfi', 'rfis', 'request for information', 'question', 'queries', 'query'],
    summary:
      'A question somebody is blocked on, asked against the record it is about — a change order, a selection, a schedule item — with a date the answer is needed by. The trade who asked cannot mark it answered: that is what makes the answer somebody else’s.',
  },
  {
    key: 'specifications',
    matches: ['specification', 'specifications', 'spec', 'specs'],
    summary:
      'What was agreed would be used or done, kept against the job so the schedule and the selections can point at it rather than restating it.',
  },
  {
    key: 'messaging',
    matches: ['message', 'messaging', 'comment', 'comments', 'mention', 'thread'],
    summary:
      'Comments on records — a daily log, a change order, a purchase order — each with an audience, so an internal note and a message to a sub are different things rather than the same thing with a flag. Naming somebody notifies them.',
  },
  {
    key: 'bids',
    matches: ['bid', 'bids', 'tender', 'quote'],
    summary:
      'What subs have quoted for work, before any of it becomes a purchase order. A sub sees their own and never another\u2019s.',
  },
  {
    key: 'reports',
    matches: ['report', 'reports', 'export', 'csv', 'spreadsheet'],
    summary:
      'Most lists export as CSV \u2014 the budget, purchase orders, the schedule, safety acknowledgements. A column that was never measured is left out of the file rather than written as zero, because a spreadsheet has no room to explain a zero.',
  },
  {
    key: 'assistant',
    matches: ['assistant', 'you', 'chat', 'ai', 'claude'],
    summary:
      'Me. I can read what you can read — every question I answer runs as you, through the same permissions — and I cannot change anything on my own. I can work out what a change would do and offer it to you: you see every date that would move, including work that moves only because it follows on, and nothing happens until you press the button. Which model answers, and what has been used this month, is under Settings → Assistant.',
  },
];

/**
 * The topics that answer a question, best first.
 *
 * Loose matching on purpose: somebody asks "how do I bill the homeowner",
 * not "invoices". Returns several, because a question often spans two —
 * "what happens when a change order is approved" is change orders and
 * budget — and handing the model both beats picking one and being wrong.
 */
export function topicsFor(question: string, limit = 3): ProductTopic[] {
  const asked = question.toLowerCase();
  const scored = PRODUCT_TOPICS.map((topic) => {
    let score = 0;
    for (const word of [...topic.matches, topic.key.toLowerCase()]) {
      if (mentions(asked, word)) score += word.length;
    }
    return { topic, score };
  })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score);

  return scored.slice(0, limit).map((entry) => entry.topic);
}

/**
 * Whether a question mentions a term **as a word**, not as a substring.
 *
 * This matched substrings first, and `po` — a perfectly reasonable thing
 * to call a purchase order — matched inside "ex**po**rt", so "time
 * tracking payroll export" came back as a question about purchase orders.
 * `ai` and `you` were waiting to do the same.
 *
 * The same class of bug as `getByLabel` matching a substring, which has
 * bitten three times in this repository. Short terms are the useful ones
 * and they are exactly the dangerous ones.
 *
 * Hyphens and apostrophes count as part of a word, so `to-do` matches as
 * itself rather than as `to` followed by `do`.
 */
function mentions(question: string, term: string): boolean {
  const escaped = term.replaceAll(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(^|[^a-z0-9'-])${escaped}([^a-z0-9'-]|$)`).test(question);
}
